<template>
    <v-col
        v-if="showFilters"
        class="d-flex ma-0 pa-0"
        :class="{'pt-2': !$vuetify.breakpoint.mdAndUp}"
    >
        <v-row no-gutters>
            <v-col
                class="shrink align-center d-flex"
                cols="12"
                :style="$scopedSlots.above?customSlotStyle:''"
            >
                <v-row class="no-gutters flex-nowrap d-flex align-center">
                    <slot
                        name="above"
                        v-bind="scopedSlotProps"
                    ></slot>
                </v-row>
            </v-col>
            <v-col
                class="shrink align-center d-flex align-self-start"
                :style="$scopedSlots.left?customSlotStyle:''"
            >
                <v-row class="no-gutters flex-nowrap align-center d-flex">
                    <slot
                        name="left"
                        v-bind="scopedSlotProps"
                    ></slot>
                </v-row>
            </v-col>
            <v-col class="grow">
                <v-row
                    class="no-gutters"
                    justify="start"
                >
                    <template v-for="(filter, index) in visibleFilters">
                        <v-col
                            :key="index"
                            :class="fullWidthFilters?'col-12':'shrink'"
                        >
                            <component
                                :is="filter.component"
                                :filter-key="filter.key"
                                :value="getFilterValueFromUrl(filter)"
                                :items="getFilterItems(filter)"
                                :loading="filter.loading"
                                :multi="filter.multi"
                                :label="$t(filter.label)"
                                :menu-state="menuStates[filter.key]"
                                :strings="friendlyLabelStrings"
                                :searchable="filter.searchable"
                                :name-key="filter.nameKey"
                                :value-key="filter.valueKey"
                                :hide-menu="filter.hideMenu"
                                :avatars="filter.avatars"
                                :deselect="filter.deselect"
                                :extra-items="filter.extraItems"
                                :hide-calendars="filter.hideCalendars"
                                :open-on-right="menuOpenRight"
                                :show-label-count-from="showLabelCountFrom"
                                show-selected-first
                                :lazy="lazyFilters"
                                @input="onComponentInput($event, filter)"
                                @update:menuState="updateMenuState(filter, $event)"
                                @friendly_label="onComponentFriendlyLabelEmit($event, filter)"
                            >
                                <!-- Template for when rendered component has an activator-->
                                <template
                                    v-if="filter.hasActivator"
                                    v-slot="{ on }"
                                >
                                    <FilterSelectButton
                                        :icon="filter.icon"
                                        :icon-size="filter.iconSize || '20'"
                                        :label="getFilterFriendlyLabel(filter) || $t(filter.label)"
                                        :filter-active-class="getFilterActiveClass(filter)"
                                        :show-label="true"
                                        :on="on"
                                        :focused="menuStates[filter.key]"
                                        :icon-color="getFilterActiveClass(filter) ? 'primary' : filter.iconColor"
                                        :transparent="transparent"
                                        :full-width="fullWidthFilters"
                                        :custom-style="customFilterStyle"
                                        remove-icon="X"
                                        :removable="isFilterRemovable(filter.key)"
                                        @remove="onFilterRemove(filter)"
                                    />
                                </template>
                            </component>
                        </v-col>
                    </template>
                </v-row>
            </v-col>
            <v-col
                class="shrink align-center d-flex align-self-start mt-1"
                :style="$scopedSlots.right?customSlotStyle:''"
            >
                <v-row class="no-gutters flex-nowrap align-center d-flex">
                    <slot
                        name="right"
                        v-bind="scopedSlotProps"
                    ></slot>
                </v-row>
            </v-col>
            <v-col
                class="shrink align-center d-flex"
                cols="12"
                :style="$scopedSlots.below?customSlotStyle:''"
            >
                <v-row class="no-gutters flex-nowrap align-center d-flex">
                    <slot
                        name="below"
                        v-bind="scopedSlotProps"
                    ></slot>
                </v-row>
            </v-col>
        </v-row>
    </v-col>
</template>

<script>

    /**
     * This component should be in charge of:
     *      rendering filters based on url
     *      changing url on filter input
     *      managing hidden and visible filters
     *      emitting events if the route changes
     */


import Vue from "vue";
import { computed, isRef, ref, watch } from "@vue/composition-api";
import { $t } from "@/i18n";
import FilterSelectButton from "./components/FilterSelectButton";
import AddFilterButton from "./components/AddFilterButton";
import DropMenu from "./components/DropMenu";
import { deepClone } from "@/helpers/helpers";
import {
    convertQueryStringToParamsObject,
    deleteUrlParameter,
    getFullQuery,
    getUrlParameter,
    setUrlParameter,
    setUrlParametersFromObject
} from "@/components/NewFilterBar/FilterBar";

export default {

    name: "NewFilterBar",
    components: {
        FilterSelectButton,
        DropMenu,
        AddFilterButton
    },
    props: {
        availableFilters: {
            type: Array,
            required: false,
            default: () => []
        },
        transparent: {
            /**
             *  Gives FilterSelectButton a transparent background
             */

            type: Boolean,
            required: false,
            default: false
        },
        showFilters: {
            type: Boolean,
            default: true
        },
        fullWidthFilters: {
            type: Boolean,
            default: false
        },
        menuOpenRight: {
            type: Boolean,
            default: false
        },
        customFilterStyle: {
            type: [String, Object],
            default: ''
        },
        customSlotStyle: {
            type: [String, Object],
            default: ''
        },
        lazyFilters: {
            type: Boolean,
            default: true
        },
        // This value determines when multiselect shall display choices as number
        showLabelCountFrom: {
            type: String,
            default: "2"
        },
        beforeRouteChange: {
            type: Function,
            default: () => {}
        },
        openMenuOnAdd:{
            type: Boolean,
            default: true
        }
    },
    setup(props, ctx) {
        const visibleFilters = ref([])
        const hiddenFilters = ref([])
        const menuStates = ref({})

        const friendlyLabels = ref({});
        const friendlyLabelStrings = {
            toggle: $t("select/unselect all"),
            several_values: $t("several values"),
            values: $t("values"),
            is: $t("is"),
            and: $t("and"),
            or: $t("or"),
            add_filter: $t("Add filter"),
            save_filter: $t("Save"),
            update: $t("Update"),
            today: $t("Today"),
            yesterday: $t("Yesterday"),
            tomorrow: $t("Tomorrow"),
            forever: $t("forever"),
            last_week: $t("Last week"),
            this_week: $t("This week"),
            next_week: $t("Next week"),
            last_month: $t("Last month"),
            this_month: $t("This month"),
            next_month: $t("Next month"),
            last_year: $t("Last year"),
            this_year: $t("This year"),
            this_quarter: $t("This quarter"),
            next_quarter: $t("Next quarter"),
            last_quarter: $t("Last quarter")
        };

        const getFilterItems = filter => {
            /**
             * Get filter items from objects, arrays, functions
             * Remove undefined items
             * Translate item.name if exists
             */
            if (isRef(filter.items)) {
                return filter.items.value;
            }
            if (typeof filter.items === "function") {
                return filter.items();
            }
            if (!filter.items) {
                return []
            }

            let items = filter.items;
            for (let i in items) {
                if (items[i]['name'] !== undefined && items[i]['name']) {
                    // notice :: translate dropdown 'name'
                    items[i]['name'] = '' + $t(items[i]['name']);
                }
            }
            return items;
        };

        const setVisibleFilters = function(){
            /**
             *  Parses the route and determines what filters to display.
             *  Filter is displayed if key is in url
             *
             */
            let params = convertQueryStringToParamsObject(getFullQuery())

            if (params){
                let paramKeys = Object.keys(params)

                let visible_keys = visibleFilters.value.map((filter) => {
                    return filter.key
                })
                let new_filters = props.availableFilters.filter(filter => {
                    return paramKeys.includes(filter.key)
                })
                let new_keys = new_filters.map((filter) => {
                    return filter.key
                })
                // compare current and new filters
                if (visible_keys.sort().join(',') === new_keys.sort().join(',')){
                    // No new filters
                    return
                }
                visibleFilters.value = new_filters
            }
        }

        const setHiddenFilters = function() {
            /**
             *  Parses the route and determines what filters to hide.
             *  Filter is hidden if key is not in url
             */
            let params = convertQueryStringToParamsObject(getFullQuery())

            if (params){
                let paramKeys = Object.keys(params)
                hiddenFilters.value = props.availableFilters.filter(filter => {
                    return !paramKeys.includes(filter.key)
                })
            }

        }

        const getFilterObject = key => {
            /**
             * Find specific filter object from availableFilters
             */

            const filter = props.availableFilters.find(i => i.key === key);
            if (filter === undefined) {
                throw new Error(
                    `Could not find object with internal 'key' of '${key}' in availableFilters. Make sure you have defined the filter before using it.`
                );
            }

            return filter;
        };


        function getFilterValueFromUrl(filter) {
            /**
             * Find specific filter value from url
             */
            let params = convertQueryStringToParamsObject(getFullQuery())

            if (filter.valueKey && !filter.multi) {
                // Value is an object
                try {
                    let value = getFilterItems(filter).find(
                        i => params[filter.key] && String(i[filter.valueKey]) === String(params[filter.key])
                    )
                    return value ? [value]: []
                } catch (ee) {
                    console.error(deepClone(filter))
                }
            }

            if (filter.valueKey && filter.multi) {
                // Value is an array of objects
                return getFilterItems(filter).filter(i => {
                    if (Array.isArray(i[filter.valueKey])) {
                        return params[filter.key].filter(x => i[filter.valueKey].includes(x)).length
                    } else {
                        return params[filter.key] && params[filter.key].includes(i[filter.valueKey])
                    }
                }

                );
            }
            // Value is a string in root level
            return params[filter.key]
        }

        function getFilterActiveClass(filter) {
            /**
             *  Get filter class by value
             */
            let result = "";
            let css = "filter-active-class"
            let val = getFilterValueFromUrl(filter);
            if (filter.notActive) result = ""
            else if (Array.isArray(val) && val.length) result = css;
            else if (val && val.constructor === Object && val[filter.valueKey]) result = css;
            else if (val && val.constructor === Number) result = css;
            return result;
        }
        function onComponentInput(e, filter) {
            /**
             *  Update url according to filter input
             */
            ctx.emit("before_filter_input", filter);
            if (filter.date) {
                setUrlParametersFromObject({ [filter.key]: e, ...props.filterInputParams })
            } else if (filter.valueKey) {
                // Emitted value is in an array of objects.;
                setUrlParametersFromObject({ [filter.key]: e.map(i => i[filter.valueKey]), ...props.filterInputParams })
            } else {

                // Emitted value is a string in root level
                setUrlParametersFromObject({ [filter.key]: e, ...props.filterInputParams })
            }
            ctx.emit("after_filter_input", filter);
        }

        function onComponentFriendlyLabelEmit(e, filter) {
            /**
             * Filters emit updated labels on input, we save this to an object and
             * use it in getFilterFriendlyLabel
             */

            friendlyLabels.value[filter.key] = $t(e);
        }

        function getFilterFriendlyLabel(filter) {
            /**
             * Create the text displayed on the filter button
             * "Prefix: value" or "Value"
             */

            let label = '';
            if (filter.prefix && friendlyLabels.value[filter.key]) {
                let prefix_label = $t(filter.label);
                let value_label = $t(friendlyLabels.value[filter.key]);
                label = `${prefix_label}: ${value_label}`;
                return label;
            }

            if (filter.replaceLabelWithValue) {
                const value = getFilterValueFromUrl(filter);
                if (value && value[filter.nameKey]) {
                    label = $t(value[filter.nameKey])
                    return label;
                }
            }
            label = $t(friendlyLabels.value[filter.key]);
            return label;
        }

        function onFilterAdd(filterObj) {
            /**
             * Update url whenever a filter is added
             */

            if (getUrlParameter(filterObj.key) !== undefined) return;

            const extraFilterDefaultValue = getFilterObject(filterObj.key).value

            if (!extraFilterDefaultValue) {
                throw new Error(
                    `Could not find default value for ${filterObj.key}`
                );
            }
            setUrlParameter(filterObj.key, extraFilterDefaultValue)

            if (props.openMenuOnAdd){
                openMenu(filterObj)
            }

        }

        function onFilterRemove(filterObj) {
            /**
             * Update url when filter is removed
             */
            ctx.emit("removing_filter", filterObj.key);
            deleteUrlParameter(filterObj.key)
        }

        function onFiltersSave() {
            /**
             * Handle saving filters as bookmark
             */

            ctx.emit("save_filters", getFullQuery());
        }

        function isFilterRemovable(key){
            let filter_object = getFilterObject(key)
            return !filter_object.default
        }


        const scopedSlotProps = computed(() => {
            /**
             * Dynamic props for the slots on the left, right, above and below the filterbar element
             */

            return {
                filters: hiddenFilters.value,
                filterAdd: onFilterAdd
            }
        })

        function emitRouteChange(){
            ctx.emit("route_change", "?" + getFullQuery());
        }

        function openMenu(filter){
            setTimeout(() => {
                updateMenuState(filter, true)
            }, 100)
        }

        function shouldOpenMenu(filter){
            return getFilterItems(filter).length
        }

        function updateMenuState(filter, value){
            if (shouldOpenMenu(filter)){
                Vue.set(menuStates.value, filter.key, value)
            }
        }

        watch("$route.query", (val, lastval) => {
            /**
             * Watch for any changes in url query and update visible and hidden filters
             * ( If visible, filters will automatically search through the url to get their values )
             * Emit event if route changes
             */

            props.beforeRouteChange()

            setVisibleFilters()
            setHiddenFilters()
            emitRouteChange()
        });

        return {
            visibleFilters,
            getFilterObject,
            onComponentInput,
            onComponentFriendlyLabelEmit,
            getFilterValueFromUrl,
            getFilterFriendlyLabel,
            friendlyLabels,
            friendlyLabelStrings,
            onFilterAdd,
            hiddenFilters,
            onFilterRemove,
            onFiltersSave,
            currentQueryString: getFullQuery,
            getFilterItems,
            scopedSlotProps,
            getFilterActiveClass,
            isFilterRemovable,
            updateMenuState,
            menuStates
        };
    }
};
</script>
